import type { RefObject } from 'react';
import { useEffect } from 'react';
import { getTargetFromPointerEvent } from './useDismissableLayerFix';

const DEFAULT_INSIDE_SELECTORS = [
  '[role="option"]',
  '[data-radix-popover-trigger]'
] as const;

/**
 * Variant of useDismissableLayerFix that uses a content ref instead of selectors.
 * Fixes the Radix UI DismissableLayer issue where clicking on elements that stop
 * propagation (like CommandItem) requires 2 outside clicks to close the layer.
 *
 * @see https://github.com/radix-ui/primitives/issues/2782
 *
 * @param isOpen - Whether the dismissable layer is currently open
 * @param onClose - Callback to close the dismissable layer
 * @param contentRef - Ref to an element inside the layer (used to find the Radix popper wrapper)
 * @param insideSelectors - Additional selectors; if target matches any, consider it "inside" (e.g. '[role="option"]', '[data-radix-popover-trigger]')
 */
export function useDismissableLayerFixFromRef(
  isOpen: boolean,
  onClose: () => void,
  contentRef: RefObject<HTMLElement | null>,
  insideSelectors: readonly string[] = DEFAULT_INSIDE_SELECTORS
) {
  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(e: PointerEvent) {
      const wrapper = contentRef.current?.closest(
        '[data-radix-popper-content-wrapper]'
      ) as HTMLElement | null;
      if (!wrapper) return;

      const target = getTargetFromPointerEvent(e);
      if (!target) return;

      const isInside =
        wrapper.contains(target) ||
        insideSelectors.some(sel => target!.closest(sel));

      if (!isInside) {
        onClose();
      }
    }

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [isOpen, onClose, contentRef, insideSelectors]);
}
